import { reflect } from '@effector/reflect';
import { Anchor } from '@mantine/core';
import { Link } from 'atomic-router-react';
import { combine, StoreValue } from 'effector';
import { FileSearch } from 'lucide-react';
import { forwardRef, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { authModel, User, USER_ROLES, UserRole } from 'shared/api';
import { $t, translated } from 'shared/lib';
import { accountRoute, userRoute } from 'shared/routing';
import { createRegistry, MultiSelect, RegistryRootProps, SearchInput } from 'shared/ui';
import { UserRegistryModel } from '../../model';
import cls from './user-registry.module.scss';

export type UserRegistryProps = RegistryRootProps & {
	withTitle?: boolean;
	withAccount?: boolean;
	emptyActionSlot?: ReactNode;
	topBarActionSlot?: ReactNode;
	tableTdActionSlot?: (user: User) => ReactNode;
};

export const createUserRegistry = (model: UserRegistryModel) => {
	const Registry = createRegistry(model);

	const UsernameFilter = reflect({
		view: SearchInput<'input'>,
		bind: {
			'data-test-id': 'username-search-input',
			label: translated('user-registry.filters.name.label'),
			placeholder: translated('user-registry.filters.name.placeholder'),
			className: cls.userRegistryNameFilter,
			value: model.filters.name.$value,
			onChange: (e) => model.filters.name.setValue(e.target.value),
		},
	});

	const RolesFilter = reflect({
		view: MultiSelect,
		bind: {
			id: 'roles-filter',
			label: translated('user-registry.filters.role.label'),
			placeholder: translated('user-registry.filters.role.placeholder'),
			className: cls.userRegistryCurrenciesFilter,
			value: model.filters.roles.$value.map((roles) => roles.map(String)),
			onChange: (value) => model.filters.roles.setValue(value.map(Number) as UserRole[]),
			data: combine([$t, authModel.$role]).map(([t, role]) =>
				Object.values(USER_ROLES)
					.filter((r) => (role === USER_ROLES.god ? true : r !== USER_ROLES.god))
					.map((r) => ({
						value: `${r}`,
						label: t(`user-roles.${r}`),
					})),
			),
		},
	});

	const UserRegistry = forwardRef<HTMLDivElement, UserRegistryProps>(
		(
			{
				withTitle = true,
				withAccount = true,
				emptyActionSlot,
				topBarActionSlot,
				tableTdActionSlot,
				...props
			},
			ref,
		) => {
			const { t } = useTranslation();

			return (
				<Registry.Root
					ref={ref}
					{...props}
				>
					{withTitle && <Registry.Title>{t('user-registry.title')}</Registry.Title>}
					<Registry.TopBar className={cls.userRegistryTopBar}>
						<Registry.Filters className={cls.userRegistryFilters}>
							<UsernameFilter />
							<RolesFilter />
						</Registry.Filters>
						<div className={cls.userRegistryActions}>{topBarActionSlot}</div>
					</Registry.TopBar>
					<Registry.Table>
						<Registry.Table.Colgroup>
							{withAccount && <Registry.Table.Col />}
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
						</Registry.Table.Colgroup>
						<Registry.Table.Thead>
							<Registry.Table.Tr>
								<Registry.Table.ThSorter field='name'>
									{withAccount
										? t('user-registry.table-columns.username')
										: t('user-registry.table-columns.name')}
								</Registry.Table.ThSorter>
								{withAccount && (
									<Registry.Table.Th>{t('user-registry.table-columns.account')}</Registry.Table.Th>
								)}
								<Registry.Table.Th>{t('user-registry.table-columns.role')}</Registry.Table.Th>
								<Registry.Table.Th>{t('user-registry.table-columns.note')}</Registry.Table.Th>
								<Registry.Table.Th>{t('user-registry.table-columns.actions')}</Registry.Table.Th>
							</Registry.Table.Tr>
						</Registry.Table.Thead>
						<Registry.Table.Tbody>
							<Registry.Table.TrList
								getProps={(row) => ({
									variant: row.isLocked ? 'locked' : 'default',
								})}
							>
								{(row) => (
									<>
										<Registry.Table.Td>
											<Anchor
												data-test-id={`user-registry-user-link-${row.id}`}
												component={Link<StoreValue<typeof userRoute.$params>>}
												to={userRoute}
												params={{ id: row.id }}
											>
												{row.name}
											</Anchor>
										</Registry.Table.Td>
										{withAccount && (
											<Registry.Table.Td>
												<Anchor
													data-test-id={`user-registry-account-link-${row.account?.id}`}
													component={Link}
													to={accountRoute}
													query={{ id: row.account.id }}
												>
													{row.account.name}
												</Anchor>
											</Registry.Table.Td>
										)}
										<Registry.Table.Td>{t(`user-roles.${row.role}`)}</Registry.Table.Td>
										<Registry.Table.Td>{row.note}</Registry.Table.Td>
										<Registry.Table.Td>{tableTdActionSlot?.(row)}</Registry.Table.Td>
									</>
								)}
							</Registry.Table.TrList>
						</Registry.Table.Tbody>
					</Registry.Table>
					<Registry.BottomBar>
						<Registry.Pagination />
					</Registry.BottomBar>
					<Registry.Empty
						icon={<FileSearch />}
						title={t('user-registry.empty.title')}
						message={t('user-registry.empty.message')}
					>
						{emptyActionSlot}
					</Registry.Empty>
					<Registry.Table.Empty
						icon={<FileSearch />}
						title={t('user-registry.table-empty.title')}
						message={t('user-registry.table-empty.message')}
					/>
				</Registry.Root>
			);
		},
	);

	return UserRegistry;
};
